import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import api from '@/lib/api';
import { toast } from '@/lib/toast';
import { DAY_LABELS } from '@/lib/businessHours';

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const LIMIT_FIELDS = [
  { key: 'daily', label: 'Per day' },
  { key: 'weekly', label: 'Per week' },
  { key: 'monthly', label: 'Per month' },
];

const SiteEditor = ({ site, onSaved }) => {
  const [hours, setHours] = useState(site.business_hours || {});
  const [limits, setLimits] = useState({
    daily: site.appointment_limits?.daily ?? '',
    weekly: site.appointment_limits?.weekly ?? '',
    monthly: site.appointment_limits?.monthly ?? '',
  });
  const [saving, setSaving] = useState(false);

  const setDay = (key, patch) =>
    setHours((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const appointment_limits = {};
      for (const { key } of LIMIT_FIELDS) {
        const raw = limits[key];
        const n = parseInt(raw, 10);
        appointment_limits[key] = raw !== '' && Number.isFinite(n) && n >= 1 ? n : null;
      }
      await api.put(`/sites/${site.site_id}`, {
        business_hours: hours,
        appointment_limits,
      });
      toast.success('Location settings saved');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save location settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" color="text.secondary">
        Business hours — scheduling outside these hours is still allowed, but the calendar
        flags it as outside normal business hours.
      </Typography>
      {DAY_ORDER.map((key) => {
        const day = hours[key] || { open: '09:00', close: '17:00', closed: true };
        return (
          <Stack key={key} direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={!day.closed}
                  onChange={(e) => setDay(key, { ...day, closed: !e.target.checked })}
                  size="small"
                />
              }
              label={DAY_LABELS[key]}
              sx={{ width: 150, mr: 0 }}
            />
            <TextField
              type="time"
              size="small"
              value={day.open || '09:00'}
              disabled={!!day.closed}
              onChange={(e) => setDay(key, { ...day, open: e.target.value })}
              inputProps={{ step: 300 }}
            />
            <Typography variant="body2" color="text.secondary">
              to
            </Typography>
            <TextField
              type="time"
              size="small"
              value={day.close || '17:00'}
              disabled={!!day.closed}
              onChange={(e) => setDay(key, { ...day, close: e.target.value })}
              inputProps={{ step: 300 }}
            />
            {day.closed && (
              <Typography variant="caption" color="text.secondary">
                Closed
              </Typography>
            )}
          </Stack>
        );
      })}

      <Divider />

      <Typography variant="subtitle2" color="text.secondary">
        Client appointment limits — maximum appointments per client at this location. Leave
        blank for no limit.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {LIMIT_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            label={label}
            type="number"
            size="small"
            inputProps={{ min: 1, step: 1 }}
            placeholder="No limit"
            InputLabelProps={{ shrink: true }}
            value={limits[key]}
            onChange={(e) => setLimits((prev) => ({ ...prev, [key]: e.target.value }))}
          />
        ))}
      </Stack>

      <Box>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save location settings'}
        </Button>
      </Box>
    </Stack>
  );
};

const LocationSettings = () => {
  const [sites, setSites] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/sites');
      setSites(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load locations');
      setSites([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (sites === null) return <Skeleton variant="rounded" height={220} />;
  if (sites.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No locations configured.
      </Typography>
    );
  }

  return (
    <Stack spacing={4}>
      {sites.map((site) => (
        <Box key={site.site_id}>
          {sites.length > 1 && (
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              {site.name}
              {site.is_primary ? ' (primary)' : ''}
            </Typography>
          )}
          <SiteEditor site={site} onSaved={load} />
        </Box>
      ))}
    </Stack>
  );
};

export default LocationSettings;
